// Klinik Yönetimi › WhatsApp (whatsapp:manage) — kliniğin kendi WhatsApp Business
// hattını Meta Embedded Signup ile bağlama, durum, şablonlar, bağlantıyı kesme.
// Spec: docs/superpowers/specs/2026-10-06-whatsapp-business-design.md

import React, { useState } from "react";
import { Alert, Box, Link, Skeleton } from "@mui/material";
import { WhatsApp as WhatsAppIcon } from "@mui/icons-material";
import { EmptyState } from "../../../components/atoms";
import { useAlert } from "../../../context/AlertProvider";
import { COLORS, TYPO, CARD_GAP, CARD_PAGE_PAD } from "../../../styles/uiConstants";
import { accountState, declarationAccepted } from "../../../constants/whatsapp";
import {
  useAcceptWhatsAppDeclarationMutation,
  useConnectWhatsAppMutation,
  useDisconnectWhatsAppMutation,
  useSyncWhatsAppTemplatesMutation,
  useWhatsAppStatusQuery,
} from "../../../store/whatsappApi";
import ConnectPanel from "./ConnectPanel";
import ConnectedPanel from "./ConnectedPanel";
import useEmbeddedSignup from "./useEmbeddedSignup";

const errText = (e, fallback) => e?.data?.message || fallback;

export default function WhatsAppTab({ token, palette }) {
  const { showSuccess, showError } = useAlert();
  const { data, isLoading, isError, refetch } = useWhatsAppStatusQuery(
    { token },
    { skip: !token, refetchOnMountOrArgChange: true }
  );
  const status = data?.data || null;
  const account = status?.account || null;
  const declaration = status?.declaration || null;
  const state = accountState(account);
  const accepted = declarationAccepted(declaration, account);

  const [acceptDeclaration, { isLoading: accepting }] = useAcceptWhatsAppDeclarationMutation();
  const [connect] = useConnectWhatsAppMutation();
  const [disconnect, { isLoading: disconnecting }] = useDisconnectWhatsAppMutation();
  const [syncTemplates, { isLoading: syncing }] = useSyncWhatsAppTemplatesMutation();
  const { start, abort } = useEmbeddedSignup();

  const [connecting, setConnecting] = useState(false);
  const [progress, setProgress] = useState("");
  // Meta penceresi açıkken "Vazgeç" gösterilir; sunucu adımında gösterilmez
  const [awaitingMeta, setAwaitingMeta] = useState(false);
  const [connectError, setConnectError] = useState("");
  const [connectInfo, setConnectInfo] = useState("");
  // "coexistence" = telefondaki WhatsApp Business numarası (uygulamada kalır), "new" = yalnız panel
  const [connectMode, setConnectMode] = useState("coexistence");
  // Kayıt ve ardından durumun yeniden okunması bitene kadar kutuda halka döner
  // (aradaki anda kutu boş ve tıklanabilir görünmesin).
  const [acceptRequested, setAcceptRequested] = useState(false);
  const acceptBusy = accepting || (acceptRequested && !accepted);

  const handleAccept = async () => {
    setAcceptRequested(true);
    try {
      await acceptDeclaration({ token, version: declaration?.version }).unwrap();
      await refetch();
      showSuccess("Beyan kabul edildi");
    } catch (e) {
      showError(errText(e, "Beyan kaydedilemedi"));
    } finally {
      setAcceptRequested(false);
    }
  };

  const handleConnect = async () => {
    if (!status?.appId || !status?.configId) {
      setConnectError("WhatsApp bağlantı ayarları eksik. Sistem yöneticinize başvurun.");
      return;
    }
    setConnectError("");
    setConnectInfo("");
    setConnecting(true);
    setProgress("Meta penceresinde işlemi tamamlayın…");
    setAwaitingMeta(true);
    try {
      const res = await start({
        appId: status.appId,
        configId: status.configId,
        graphVersion: status.graphVersion,
        coexistence: connectMode === "coexistence",
      });
      setAwaitingMeta(false);
      if (res.status === "cancel") {
        setConnectInfo("Bağlantı tamamlanmadı. Hazır olduğunuzda yeniden deneyebilirsiniz.");
        return;
      }
      if (res.status === "error") {
        setConnectError(
          res.reason === "sdk"
            ? "Meta bağlantı penceresi yüklenemedi. Tarayıcınızın açılır pencere veya içerik engelleyicisini kontrol edin."
            : "Meta'dan yetki alınamadı. Lütfen yeniden deneyin."
        );
        return;
      }
      setProgress("Hesabınız bağlanıyor, şablonlar oluşturuluyor…");
      await connect({
        token,
        code: res.code,
        wabaId: res.wabaId || undefined,
        phoneNumberId: res.phoneNumberId || undefined,
        coexistence: res.coexistence || undefined,
      }).unwrap();
      showSuccess("WhatsApp hattınız bağlandı");
    } catch (e) {
      setConnectError(errText(e, "WhatsApp bağlanamadı. Lütfen yeniden deneyin."));
    } finally {
      setConnecting(false);
      setAwaitingMeta(false);
      setProgress("");
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect({ token }).unwrap();
      showSuccess("WhatsApp bağlantısı kesildi");
      return true;
    } catch (e) {
      showError(errText(e, "Bağlantı kesilemedi"));
      return false;
    }
  };

  const handleSync = async () => {
    try {
      await syncTemplates({ token }).unwrap();
      showSuccess("Şablon durumları güncellendi");
    } catch (e) {
      showError(errText(e, "Şablonlar yenilenemedi"));
    }
  };

  const shell = (children) => (
    <Box
      sx={{
        ...CARD_PAGE_PAD,
        bgcolor: COLORS.slate50,
        minHeight: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: CARD_GAP,
      }}
    >
      {children}
    </Box>
  );

  if (isLoading && !status) {
    return shell(
      <>
        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />
      </>
    );
  }

  if (isError && !status) {
    return shell(
      <EmptyState
        variant="illustrated"
        icon={<WhatsAppIcon />}
        title="WhatsApp durumu alınamadı"
        description="Bağlantı bilgisi şu an yüklenemiyor. Biraz sonra yeniden deneyin."
        action={
          <Link component="button" type="button" onClick={refetch} sx={{ color: palette.primary, fontSize: TYPO.body }}>
            Yeniden dene
          </Link>
        }
      />
    );
  }

  if (status && status.configured === false) {
    return shell(
      <Alert severity="info" sx={{ borderRadius: 2, fontSize: TYPO.body }}>
        Sistem yöneticisi WhatsApp kurulumunu henüz tamamlamadı.
      </Alert>
    );
  }

  if (state === "suspended") {
    return shell(
      <Alert severity="warning" sx={{ borderRadius: 2, fontSize: TYPO.body }}>
        Bağlantı sistem yöneticisi tarafından askıya alındı
        {account?.suspension?.reason ? `: ${account.suspension.reason}` : "."}
      </Alert>
    );
  }

  if (state === "connected") {
    return shell(
      <ConnectedPanel
        account={account}
        clinicPhone={status?.clinicPhone || null}
        onDisconnect={handleDisconnect}
        disconnecting={disconnecting}
        onSync={handleSync}
        syncing={syncing}
        palette={palette}
      />
    );
  }

  return shell(
    <>
      {state === "broken" ? (
        <Alert severity="error" sx={{ borderRadius: 2, fontSize: TYPO.body }}>
          Bağlantı koptu. Meta hesabınızdan Pupill'in erişimi kaldırılmış olabilir. Aşağıdan yeniden bağlayabilirsiniz.
        </Alert>
      ) : null}
      <ConnectPanel
        declaration={declaration}
        account={account}
        accepted={accepted}
        onAccept={handleAccept}
        accepting={acceptBusy}
        onConnect={handleConnect}
        connecting={connecting}
        progressText={progress}
        onCancel={awaitingMeta ? abort : undefined}
        info={connectInfo}
        error={connectError}
        reconnect={state === "broken"}
        clinicPhone={status?.clinicPhone || null}
        mode={connectMode}
        onModeChange={setConnectMode}
        palette={palette}
      />
    </>
  );
}
